import { useState, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { Button, Textarea } from "components/ui";
import { Listbox } from "components/shared/form/Listbox";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { getStageAndSubStages } from "services/leadStageService";
import { bulkUpdateLeadStage } from "services/leadsService";
import { isJobStage } from "utils/leadStageHelpers";
import { toast } from "sonner";

/**
 * Generic "pick a non-job Lead stage and apply it" modal, shared by every
 * job-side flow that needs to sync a lead's stage back to a sales stage
 * (revert-to-lead, or nudging the user after manually cancelling a job).
 * Copy/labels are parameterized since the two callers frame the action
 * differently; the picker + submit behavior is identical.
 *
 * When `onCancel` is provided, backing out isn't a free close: mirroring
 * the Convert-to-Job flow (where backing out reverts the lead's stage),
 * backing out of THIS picker instead reverts whatever change made it
 * mandatory (e.g. the job status change that triggered it) - after a
 * confirmation step, same as ConvertToJobModal's cancel warning.
 */
export function UpdateLeadStageModal({
  show,
  onClose,
  leadId,
  leadTitle,
  leadData,
  icon: Icon = ArrowUturnLeftIcon,
  title,
  description,
  submitLabel,
  confirmTitle,
  confirmDescription,
  confirmActionText,
  onSuccess,
  onCancel,
  cancelWarningTitle = "Cancel Stage Update?",
  cancelWarningDescription = "If you continue without selecting a stage, this change will be reverted.",
  cancelWarningActionText = "Yes, Revert",
}) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedSubStage, setSelectedSubStage] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const isInvalid = (val) =>
    !val ||
    typeof val !== "string" ||
    ["unknown", "unknown lead", "unknown user", "n/a", "-", "null", "undefined"].includes(val.trim().toLowerCase());

  const resolvedLeadTitle =
    (!isInvalid(leadTitle) ? leadTitle.trim() : null) ||
    (!isInvalid(leadData?.title) ? leadData.title.trim() : null) ||
    (!isInvalid(leadData?.Title) ? leadData.Title.trim() : null) ||
    (!isInvalid(leadData?.name) ? leadData.name.trim() : null) ||
    (!isInvalid(leadData?.FullName) ? leadData.FullName.trim() : null) ||
    (!isInvalid(leadData?.clientContact) ? leadData.clientContact.trim() : null) ||
    null;

  useEffect(() => {
    if (show) {
      setSelectedStage(null);
      setSelectedSubStage(null);
      setRemarks("");
      setShowCancelWarning(false);
      loadStages();
    }
  }, [show]);

  const loadStages = async () => {
    setLoading(true);
    try {
      const stagesData = await getStageAndSubStages({ pageSize: 100 });
      // Only non-job stages are valid targets here - moving "into" one of
      // the 3 job stages through this picker doesn't make sense.
      const nonJobStages = (stagesData || [])
        .filter((s) => s?.isActive && !isJobStage(s.stageConstant))
        .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));
      setStages(nonJobStages);
    } catch (error) {
      console.error("Failed to load stages", error);
    } finally {
      setLoading(false);
    }
  };

  const availableSubStages = selectedStage?.subStages?.filter((s) => s.isActive) || [];

  const handleStageChange = (stage) => {
    setSelectedStage(stage);
    setSelectedSubStage(null);
  };

  const handleSubmitClick = () => {
    if (!selectedStage) {
      toast.info("Please select a lead stage");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirmSubmit = async () => {
    setConfirmLoading(true);
    const result = await bulkUpdateLeadStage({
      leadIds: Array.isArray(leadId) ? leadId : [leadId],
      stageId: selectedStage.stageId,
      subStageId: selectedSubStage?.subStageId || null,
      remarks,
    });
    setConfirmLoading(false);
    setShowConfirm(false);
    if (result.success) {
      toast.success("Stage updated successfully");
      onClose();
      if (onSuccess) onSuccess();
    } else {
      toast.error(result.error || "Failed to update stage");
    }
  };

  const confirmMessages = {
    pending: {
      title: confirmTitle,
      description: confirmDescription,
      actionText: confirmActionText,
    },
  };

  const requestClose = () => {
    if (onCancel) {
      setShowCancelWarning(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = async () => {
    setCancelLoading(true);
    await onCancel();
    setCancelLoading(false);
    setShowCancelWarning(false);
    onClose();
  };

  const cancelWarningMessages = {
    pending: {
      title: cancelWarningTitle,
      description: cancelWarningDescription,
      actionText: cancelWarningActionText,
    },
  };

  return (
    <>
      <Transition appear show={show} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-[9000] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
          onClose={() => {}}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 transition-opacity dark:bg-black/40" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="relative flex w-full max-w-md flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-dark-700">
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-4 dark:border-dark-600 dark:bg-dark-800">
                <div className="flex items-center gap-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/20">
                    <Icon className="h-4 w-4 text-primary dark:text-primary-400" />
                  </div>
                  <DialogTitle as="h3" className="text-sm font-bold uppercase tracking-wider text-gray-900 dark:text-white">
                    {title}
                  </DialogTitle>
                </div>
                <button
                  onClick={requestClose}
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-dark-600 dark:hover:text-white"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-4 px-5 py-5">
                {resolvedLeadTitle && (
                  <div className="rounded-lg bg-gray-50 px-3 py-2 border border-gray-100 dark:bg-dark-800 dark:border-dark-600">
                    <span className="text-[11px] font-semibold text-gray-500 dark:text-dark-300 uppercase tracking-wider">Lead: </span>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">{resolvedLeadTitle}</span>
                  </div>
                )}
                <p className="text-xs leading-normal text-gray-500 dark:text-dark-300">{description}</p>

                <Listbox
                  label="Lead Stage *"
                  data={stages}
                  value={selectedStage}
                  onChange={handleStageChange}
                  displayField="stageName"
                  placeholder="Select Lead Stage"
                  disabled={loading}
                />

                <Listbox
                  label="Sub Stage (Optional)"
                  data={availableSubStages.sort((a, b) => a.subStageOrder - b.subStageOrder)}
                  value={selectedSubStage}
                  onChange={setSelectedSubStage}
                  displayField="subStageName"
                  placeholder={
                    !selectedStage
                      ? "Select a stage first"
                      : availableSubStages.length === 0
                        ? "No sub-stages available"
                        : "Select Sub Stage (Optional)"
                  }
                  disabled={!selectedStage || availableSubStages.length === 0}
                />

                <Textarea
                  label="Notes (Optional)"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Add notes..."
                  rows={3}
                />
              </div>

              <div className="flex items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/50 px-5 py-4 dark:border-dark-600 dark:bg-dark-800/50">
                <Button onClick={requestClose} variant="outlined" className="px-4 py-2 text-xs font-bold">
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitClick}
                  color="primary"
                  disabled={loading || !selectedStage}
                  className="px-4 py-2 text-xs font-bold"
                >
                  {submitLabel}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>

      <ConfirmModal
        show={showConfirm}
        onClose={() => setShowConfirm(false)}
        onOk={handleConfirmSubmit}
        confirmLoading={confirmLoading}
        messages={confirmMessages}
      />

      {onCancel && (
        <ConfirmModal
          show={showCancelWarning}
          onClose={() => setShowCancelWarning(false)}
          onOk={handleConfirmCancel}
          confirmLoading={cancelLoading}
          messages={cancelWarningMessages}
        />
      )}
    </>
  );
}
